/**
 * Supplier-invoice upload. Accepts an XML and/or a PDF.
 *
 * XML → the server parses it in the background; this polls the job so the user
 * sees the row fill itself in. PDF-only → stored immediately and the invoice
 * waits in `pendiente` for someone to type the fields.
 */
import { useEffect, useRef, useState } from 'react';
import { Button, Callout, Text, Title } from '@tremor/react';
import { uploadPayable, fetchParseJob } from '../api/payables';
import { t } from '../lib/i18n';
import Modal from './Modal';
import PayableFilePicker from './PayableFilePicker';

interface Props {
  /** PO context, when launched from the purchase-order detail modal. */
  sid?: number;
  onum?: number;
  onClose: () => void;
  onDone: (invoiceId: string) => void;
}

export default function PayableUploadModal({ sid, onum, onClose, onDone }: Props) {
  const [xml, setXml] = useState<File | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  const submit = async () => {
    if (!xml && !pdf) { setErr(t.payables.uploadHint); return; }
    setBusy(true);
    setErr(null);
    try {
      const { invoice_id, job_id } = await uploadPayable(
        { xml: xml ?? undefined, pdf: pdf ?? undefined },
        { sid, onum },
      );
      if (!job_id) { onDone(invoice_id); return; }
      setStatus(t.payables.parsing);
      // Poll the parse job. The work is milliseconds; this loop exists so a
      // slow or failed parse is visible rather than silent.
      for (let i = 0; i < 100 && !cancelled.current; i++) {
        const job = await fetchParseJob(job_id);
        if (job.state === 'done') { onDone(invoice_id); return; }
        if (job.state === 'error') {
          setErr(`${t.payables.parseFailed}: ${job.error ?? ''}`);
          return;
        }
        await new Promise((r) => setTimeout(r, 200));
      }
      // Never finished in time — the row still exists, so hand it over anyway.
      onDone(invoice_id);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose} ariaLabel={t.payables.upload} size="lg" panelClassName="p-5">
      <Title>{t.payables.upload}</Title>
      <Text className="mt-1">{t.payables.uploadHint}</Text>

      {onum ? (
        <div className="mt-3 inline-flex items-center gap-2 rounded-tremor-small bg-tremor-background-muted px-2.5 py-1">
          <span className="text-tremor-label text-tremor-content">{t.payables.orden}</span>
          <span className="text-tremor-default font-medium text-tremor-content-strong">{onum}</span>
        </div>
      ) : null}

      {/* Side by side on desktop, stacked on a phone — the two files are peers,
          and either one alone is a valid upload. */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <PayableFilePicker
          label={t.payables.xmlLabel}
          hint={t.payables.dropHint}
          browseLabel={t.payables.browseLabel}
          accept=".xml,text/xml,application/xml"
          ariaLabel="XML"
          file={xml}
          onPick={setXml}
          disabled={busy}
        />
        <PayableFilePicker
          label={t.payables.pdfLabel}
          hint={t.payables.dropHint}
          browseLabel={t.payables.browseLabel}
          accept="application/pdf,.pdf"
          ariaLabel="PDF"
          file={pdf}
          onPick={setPdf}
          disabled={busy}
        />
      </div>

      <Text className="mt-3 text-tremor-label">{t.payables.uploadNote}</Text>

      {status ? <Callout title={status} color="blue" className="mt-4" /> : null}
      {err ? <Callout title={t.common.error} color="red" className="mt-4">{err}</Callout> : null}

      <div className="mt-5 flex gap-2 justify-end">
        <Button variant="light" onClick={onClose} disabled={busy}>
          {t.payables.cancelar}
        </Button>
        <Button onClick={submit} loading={busy} disabled={busy || (!xml && !pdf)}>
          {t.payables.upload}
        </Button>
      </div>
    </Modal>
  );
}
